import {patchState, signalStore, withMethods, withState} from '@ngrx/signals';
import {inject} from '@angular/core';
import {
  CreateOptionTypeDto,
  OptionClient,
  SubmitOptionDto,
  UpdateOptionTypeDto,
} from 'src/app/core/services/service-clients';
import {firstValueFrom} from 'rxjs';
import {OptionState} from './models/option.model';

const initialState: OptionState = {
  options: [],
  optionTypes: [],
  loading: false
};

export const optionStore = signalStore(
  withState<OptionState>(initialState),

  withMethods((store) => {
    const optionClient = inject(OptionClient);

    async function refreshAll() {
      const [options, optionTypes] = await Promise.all([
        firstValueFrom(optionClient.get()),
        firstValueFrom(optionClient.getTypes()),
      ]);
      patchState(store, {options, optionTypes});
    }

    return {
      // Options methods
      async loadOptions() {
        patchState(store, {loading: true});
        try {
          const options = await firstValueFrom(optionClient.get());
          patchState(store, {options, loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },

      async addOption(option: SubmitOptionDto) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(optionClient.post(option));
          await refreshAll();
          patchState(store, {loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },

      async updateOption(option: SubmitOptionDto) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(optionClient.put(option));
          await refreshAll();
          patchState(store, {loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },

      async deleteOption(optionId: number) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(optionClient.delete(optionId));
          await refreshAll();
          patchState(store, {loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },

      // Option Types methods
      async loadOptionTypes() {
        patchState(store, {loading: true});
        try {
          const optionTypes = await firstValueFrom(optionClient.getTypes());
          patchState(store, {optionTypes, loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },

      async addOptionType(dto: CreateOptionTypeDto) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(optionClient.postType(dto));
          await refreshAll();
          patchState(store, {loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },

      async updateOptionType(dto: UpdateOptionTypeDto) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(optionClient.putType(dto));
          await refreshAll();
          patchState(store, {loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },

      async deleteOptionType(optionTypeId: number) {
        patchState(store, {loading: true});
        try {
          await firstValueFrom(optionClient.deleteType(optionTypeId));
          await refreshAll();
          patchState(store, {loading: false});
        } catch {
          patchState(store, {loading: false});
          return false;
        }
        return true;
      },
    };
  })
);
